import React, { useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Icon, SemanticICONS } from 'semantic-ui-react';
import { emailRegex } from '~/components/FormFields/constants';
import { FormFields } from '~/components/FormFields';
import {
  StyledAddButton,
  StyledCardContent,
  StyledCardHeaderRow,
  StyledEmailRow,
  StyledEmailSection,
  StyledEmailTrashButton,
  StyledIconBox,
  StyledNotificationCard,
  StyledNotificationDescription,
  StyledNotificationIcon
} from './style';

export interface NotificationCardProps {
  icon: SemanticICONS;
  title: string;
  description: string;
  emailKey: string;
  requiredKey: string;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  icon,
  title,
  description,
  emailKey,
  requiredKey
}) => {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();

  const watchField = useWatch({ control, name: requiredKey });
  const isActive = parseInt(watchField) === 1;

  const emailList = useFieldArray({ control, name: emailKey });

  useEffect(() => {
    if (isActive && emailList.fields.length === 0) {
      emailList.append({ email: '' });
    }
  }, [isActive]);

  return (
    <StyledNotificationCard $active={isActive}>
      <StyledCardContent>
        <StyledCardHeaderRow>
          <StyledIconBox $active={isActive}>
            <StyledNotificationIcon name={icon} $active={isActive} />
          </StyledIconBox>
          <div style={{ flex: 1 }}>
            <FormFields.Checkbox
              label={title}
              style={{ margin: 0 }}
              defaultValue={0}
              onChange={(_, data) => {
                setValue(requiredKey, data.checked ? 1 : 0)
                if (!data.checked) {
                  emailList.replace([])
                }
              }}
              control={control}
              name={requiredKey}
              checkboxValue={1}
            />
            <StyledNotificationDescription>{description}</StyledNotificationDescription>
          </div>
        </StyledCardHeaderRow>

        {isActive && (
          <StyledEmailSection>
            {emailList.fields.map((emailField, index) => {
              const isLast = index === emailList.fields.length - 1;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} key={emailField.id}>
                  <StyledEmailRow >
                    <Icon name='mail outline' />
                    <FormFields.Input
                      control={control}
                      name={`${emailKey}[${index}].email`}
                      placeholder={t('name@example.com')}
                      rules={{
                        required: { value: Boolean(isActive), message: t('Please enter email') },
                        pattern: { value: emailRegex, message: t('Please enter valid email') }
                      }}
                    />
                    <StyledEmailTrashButton
                      icon='trash'
                      type='button'
                      onClick={() => emailList.remove(index)}
                    />
                  </StyledEmailRow>
                  {isLast && (
                    <StyledAddButton
                      icon='plus'
                      type='button'
                      onClick={() => emailList.append({ email: '' })}
                    />
                  )}
                </div>
              );
            })}
          </StyledEmailSection>
        )}
      </StyledCardContent>
    </StyledNotificationCard>
  );
};

export default NotificationCard;
